import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Feather } from "lucide-react";
import SEO from "../components/SEO";

/**
 * Verbindliche SAT-HPA-Definition (Section 6.1 P1 · Methoden-Seite).
 * Bewusst schlank gehalten — die Tiefen-Erklärung von HTC lebt auf /sat-htc.
 * HPA ist als komplementäres Reflexionsmodell positioniert (KEINE Diagnostik).
 */
export default function HumanPotentialAnalysis() {
  return (
    <div className="min-h-screen bg-[#081328] text-white pt-32 pb-24" data-testid="hpa-page">
      <SEO
        title="SAT-HPA · Human Potential Analysis"
        description="SAT-HPA (Human Potential Analysis) ist ein Reflexions- und Orientierungsmodell der SAT Akademie. Es macht individuelles Potenzial sichtbar — als Impuls, nicht als Diagnose."
        path="/methoden/human-potential-analysis"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "url": "https://sat-akademie.com/methoden/human-potential-analysis/",
        "inLanguage": "de-CH",
        "isPartOf": { "@id": "https://sat-akademie.com/#website" },
        "about": {
          "@type": "Thing",
          "name": "Human Potential Analysis",
          "alternateName": "SAT-HPA",
          "description": "Reflexionsmodell der SAT Akademie zur Potenzialklärung."
        }
      })}} />

      <div className="container-sat max-w-4xl">
        <div className="mb-14">
          <div className="overline mb-4">Methode · SAT-HPA</div>
          <h1 className="serif text-4xl md:text-6xl font-light leading-[1.05]">
            Human <span className="italic text-[#D4AF37]">Potential</span> Analysis.
          </h1>
          <p className="serif italic text-white/70 text-xl mt-8 max-w-2xl leading-relaxed">
            Ein Reflexionsmodell der SAT Akademie, um natürliches Potenzial sichtbar zu machen.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <div>
            <div className="overline mb-3">Was HPA ist</div>
            <p className="text-white/75 leading-relaxed text-[15px]">
              Die SAT-HPA (Human Potential Analysis) ist ein von der SAT Akademie
              entwickeltes Reflexions- und Orientierungsmodell. Sie kombiniert
              Impulse aus Bewusstseinsarbeit, Human Design und systemischer
              Mustererkennung, um natürliche Talente, Entscheidungsweisen und
              individuelle Berufungsimpulse besser wahrzunehmen.
            </p>
            <p className="text-white/75 leading-relaxed text-[15px] mt-4">
              Sie ergänzt den <Link to="/sat-htc" className="text-[#D4AF37] hover:underline">SAT-HTC</Link>:
              Während der Human Transformation Code den inneren Aufbau eines Menschen
              entschlüsselt, macht die HPA das noch ungelebte Potenzial sichtbar.
            </p>
          </div>
          <div>
            <div className="overline mb-3">Was HPA nicht ist</div>
            <ul className="text-white/70 text-[15px] leading-relaxed space-y-3 border-l-2 border-[#D4AF37]/40 pl-5">
              <li>keine medizinische oder psychologische Diagnostik</li>
              <li>keine wissenschaftlich validierte Persönlichkeitsmessung</li>
              <li>kein Ersatz für Psychotherapie</li>
              <li>keine Vorhersage von Erfolg, Beziehungsverlauf oder Lebensweg</li>
              <li>keine Determinierung — der Mensch bleibt frei</li>
            </ul>
          </div>
        </div>

        <div className="rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/[0.06] to-transparent p-8 lg:p-10 mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <Compass size={22} className="text-[#D4AF37] mb-3" />
              <div className="serif text-xl mb-2">Erkennen</div>
              <p className="text-white/65 text-sm leading-relaxed">Talente, Entscheidungsmechanik und natürliche Wirkung sichtbar machen.</p>
            </div>
            <div>
              <Feather size={22} className="text-[#D4AF37] mb-3" />
              <div className="serif text-xl mb-2">Einordnen</div>
              <p className="text-white/65 text-sm leading-relaxed">Impulse in den Kontext des eigenen Lebens, der Beziehungen und der Berufung stellen.</p>
            </div>
            <div>
              <ArrowRight size={22} className="text-[#D4AF37] mb-3" />
              <div className="serif text-xl mb-2">Verkörpern</div>
              <p className="text-white/65 text-sm leading-relaxed">Erkanntes Potenzial über SAT MASTER Schritt für Schritt in den Alltag integrieren.</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-white/60 text-[14px] leading-relaxed mb-14">
          <div className="overline mb-3">Verantwortung &amp; Grenzen</div>
          <p>
            SAT-HPA ist ein Reflexionsmodell. Interpretive Komponenten aus Human Design,
            Astrologie oder Numerologie werden als Impuls verstanden, nicht als
            wissenschaftlich validierte Diagnostik. Bei psychischen, traumatischen oder
            medizinischen Themen wird ergänzend qualifizierte fachliche Unterstützung
            empfohlen.
          </p>
        </div>

        <div className="text-center">
          <Link to="/erstgespraech" className="btn-primary" data-testid="hpa-cta-erstgespraech">
            Kostenloses Erstgespräch <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
